// "Set up hosting": one agent-queue job per site that isn't set up yet (site_kind is null).
//
// The agent follows the managed-hosting skill's setup section: decide whether the site is a
// marketing site or an app, confirm the URL the checklist should load, pick the steps to skip, and
// turn on Dependabot for the repo. The spec proposes; the build writes monitored_sites and the
// GitHub settings. Same shape as Retroactive Optimization and hosting fixes: a task plus a
// fulfillment job, so the dispatcher needs nothing new.

import { supabase } from './supabase';
import { enqueueAiJob, type AiJob } from './aiJobs';
import type { HostedSite, HostingAccount } from './hosting';
import type { JobBrief } from './supabase.types';
import { PROMPT_DEFAULTS, loadPromptTemplate, renderPrompt } from './prompts';

export type HostingSetupBrief = JobBrief & {
  hosting_setup: { account_id: string; site_id: string; site_url: string };
};

const host = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');

export function formatSetupQueueNote(account: Pick<HostingAccount, 'name'>, site: HostedSite, template: string = PROMPT_DEFAULTS['hosting-setup']): string {
  const platform = site.cloudways_app_id ? 'WordPress on Cloudways' : site.netlify_site_id ? 'Netlify' : 'not linked to Cloudways or Netlify yet';
  return renderPrompt(template, { account: account.name, site_name: site.name, site_url: site.url, site_id: site.id, platform });
}

export async function enqueueHostingSetup(input: {
  account: HostingAccount;
  site: HostedSite;
  createdBy: { id: string; name: string };
}): Promise<AiJob> {
  const { account, site, createdBy } = input;
  const title = `Hosting setup — ${account.name} (${host(site.url)})`;
  const queueNote = formatSetupQueueNote(account, site, await loadPromptTemplate('hosting-setup'));
  const now = new Date();
  const { data: task, error: taskError } = await supabase
    .from('client_tasks')
    .insert({
      client_id: account.client_id,
      title,
      description: queueNote,
      status: 'In Progress',
      due_date: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12).toISOString(),
      assigned_by: createdBy.id,
      assigned_to: createdBy.id,
      assigned_to_name: createdBy.name,
    })
    .select('id')
    .single();
  if (taskError) throw taskError;
  const brief: HostingSetupBrief = { hosting_setup: { account_id: account.id, site_id: site.id, site_url: site.url } };
  return enqueueAiJob({ task_id: task.id, title, created_by: createdBy.id, queue_note: queueNote, kind: 'fulfillment', brief });
}

/** Setup jobs still being worked (anything short of complete), by site id, so a site isn't sent twice. */
export async function fetchOpenSetupJobsBySite(): Promise<Record<string, AiJob>> {
  const { data, error } = await supabase
    .from('ai_jobs')
    .select('*')
    .not('brief->hosting_setup', 'is', null)
    .not('status', 'in', '(complete,submitted,cancelled)')
    .order('created_at', { ascending: false });
  if (error) throw error;
  const bySite: Record<string, AiJob> = {};
  for (const job of (data || []) as AiJob[]) {
    const siteId = (job.brief as HostingSetupBrief | null)?.hosting_setup?.site_id;
    if (siteId && !bySite[siteId]) bySite[siteId] = job;
  }
  return bySite;
}
